import React, { useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, useTheme, Typography, CircularProgress, alpha } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { getDarkContainerSx } from '../theme/gridDarkSx';

// AG Grid Community Modules
import {
  ClientSideRowModelModule,
  CellStyleModule,
  ColumnAutoSizeModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  QuickFilterModule,
  ValidationModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  UndoRedoEditModule,
  themeQuartz,
  colorSchemeDark,
} from 'ag-grid-community';

// AG Grid Enterprise Modules
import {
  ClipboardModule,
  RangeSelectionModule,
  CellSelectionModule,
  ExcelExportModule,
  ContextMenuModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
} from 'ag-grid-enterprise';

import { createActionsColumn } from './ActionsColumn';
import { GridToolbar } from './GridToolbar';
import { QuickFilter } from './QuickFilter';
import { createSelectionColumn } from './cellRenderers';

const enterpriseModules = [
  ClipboardModule,
  RangeSelectionModule,
  CellSelectionModule,
  ExcelExportModule,
  ContextMenuModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
];

/**
 * Base AG Grid modules used by PamGrid
 */
const BASE_MODULES = [
  ClientSideRowModelModule,
  CellStyleModule,
  ColumnAutoSizeModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  QuickFilterModule,
  ValidationModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  UndoRedoEditModule,
  ...enterpriseModules,
];

/**
 * Common CSS for cell highlighting
 */
const GRID_STYLES = `
  .ag-cell-range-selected {
    background-color: rgba(120, 160, 240, 0.3) !important;
    border: 2px solid #3f7cac !important;
  }
  .ag-cell-range-selected-1 {
    background-color: rgba(120, 160, 240, 0.3) !important;
  }
  .ag-cell-range-selected-2 {
    background-color: rgba(120, 160, 240, 0.2) !important;
  }
  .ag-cell-range-selected-3 {
    background-color: rgba(120, 160, 240, 0.1) !important;
  }
  .ag-cell-range-selected-4 {
    background-color: rgba(120, 160, 240, 0.1) !important;
  }
  .ag-cell-focus {
    border: 2px solid #3f7cac !important;
    background-color: rgba(120, 160, 240, 0.4) !important;
  }
  .ag-cell-updated {
    background-color: rgba(255, 165, 0, 0.2) !important;
  }
  .ag-cell-updated-new-row {
    background-color: rgba(255, 200, 100, 0.3) !important;
  }
  .ag-cell-month-diff {
    background-color: rgba(255, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 0, 0, 0.4) !important;
  }
  .ag-cell-high-change {
    background-color: rgba(221, 160, 221, 0.5) !important;
  }
`;

// Fields that should be non-editable by default
const NON_EDITABLE_FIELDS = ['modified_by', 'modified_date'];

/**
 * PamGrid - Comprehensive AG Grid component for PAM applications
 *
 * Features:
 * - Integrated toolbar with common actions
 * - Quick filter search
 * - Proper scrolling and copy/paste
 * - Cell highlighting on selection
 * - Configurable actions column
 * - Change tracking with visual feedback
 * - Validation support
 * - Excel export
 * - Date selection
 * - Loading states
 *
 * @param {Object} props - See prop documentation below
 */
const PamGridBase = forwardRef(({
  // === DATA ===
  rowData,
  columnDefs,

  // === TOOLBAR ===
  title,
  subtitle,
  showToolbar = false,

  // File operations
  showUpload = false,
  onUpload,
  uploadLabel = 'Upload',
  uploadTooltip,

  showTemplate = false,
  onDownloadTemplate,
  templateLabel = 'Template',

  showExport = true,
  onExport,
  exportLabel = 'Export Excel',
  exportConfig = {},

  // Row operations
  showAddRow = false,
  onAddRow,
  addRowLabel = 'Add Row',

  showSave = false,
  onSave,
  changedRowCount = 0,
  isSaving = false,

  showClearFilters = true,
  // Consumed here to prevent it from leaking through to AG Grid as an
  // unknown gridOptions property. PamGrid manages its own handleClearFilters
  // internally; accepting the prop just silences the AG Grid warning.
  onClearFilters: _onClearFilters,

  // Bulk operations
  showBulkDelete = false,
  onBulkDelete,
  selectedRowCount = 0,

  // Date selection
  showDateSelect = false,
  availableDates = [],
  selectedDate = '',
  onDateChange,
  isLoadingDates = false,

  // Custom toolbar buttons
  customToolbarButtons = [],
  // Custom toolbar components (full React nodes — for stateful entries
  // like GridConfigMenu that customToolbarButtons can't express).
  customToolbarComponents = [],

  // === QUICK FILTER ===
  showQuickFilter = true,
  quickFilterText,
  onQuickFilterChange,
  quickFilterPlaceholder = 'Quick Filter...',

  // === SIZING ===
  height = 'calc(100vh - 300px)',
  rowHeight = 32,
  headerHeight = 40,

  // === ACTIONS COLUMN ===
  showActions = false,
  actions = ['history', 'edit', 'duplicate', 'delete'],
  onHistory,
  onEdit,
  onDuplicate,
  onDelete,
  isActionDisabled,
  actionsColumnWidth,

  // === CHANGE TRACKING ===
  updatedRows,
  // Row-shaped: (row) => id. PamGrid adapts it for AG Grid.
  getRowId: rowGetRowId,
  // Set only by the PamGrid wrapper, from an ``agGridProps.getRowId``: an
  // AG Grid callback, ({ data, ... }) => id, passed to AG Grid unchanged.
  agGridGetRowId,
  isNewRow,

  // === SELECTION COLUMN (PamGrid-managed, app-controlled state) ===
  showSelectionColumn = false,
  selectedRows,
  onRowSelectionChange,
  selectionColumnWidth = 50,
  isSelectionDisabled = false,

  // === NATIVE AG GRID SELECTION COLUMN (v32.2+ rowSelection.checkboxes) ===
  // Pass `selectable={true}` (or `selectable={{...overrides}}`) to opt into
  // AG Grid's built-in selection-checkbox column. Prefer this over the
  // deprecated `colDef.checkboxSelection`/`headerCheckboxSelection` flags.
  selectable = false,

  // === CELL RENDERERS ===
  cellRenderers = {},

  // === CELL EDITORS ===
  cellEditors = {},

  // === DEFAULT COLUMN DEFINITION ===
  defaultColDef: userDefaultColDef,

  // === ADDITIONAL MODULES ===
  modules: additionalModules = [],

  // === EXCEL STYLES ===
  excelStyles = [],

  // === STATE ===
  isLoading = false,
  loadingMessage = 'Loading...',

  // === CALLBACKS ===
  onCellValueChanged,
  onGridReady,
  onFirstDataRendered,
  onSelectionChanged,
  onRowClicked,
  onCellFocused,
  onCellKeyDown,
  onPasteStart,
  onPasteEnd,
  onFilterChanged,

  // === CLIPBOARD ===
  // Fires after Ctrl+C: { scope: 'range' | 'cell' }. Use this to show toast
  // notifications. PamGrid invokes copySelectedRangeToClipboard() internally;
  // callbacks should NOT call clipboard APIs themselves.
  onCopy,
  // When false, Ctrl+V is swallowed (paste blocked). Defaults to true.
  allowPaste = true,

  // === CONTAINER STYLING ===
  containerSx = {},
  gridContainerSx = {},

  // === FOOTER ===
  showFooter = true,
  footerText,

  // === INSTRUCTIONS (legacy - use showFooter instead) ===
  showInstructions = false,
  instructions,

  // === LEGEND ===
  showLegend = false,
  legendItems = [],

  // === ADDITIONAL AG GRID PROPS ===
  // Merged into PamGrid's own grid context (see mergedContext).
  context: callerContext,
  ...agGridProps
}, ref) => {
  // PamGrid's own row-shaped uses (selection column, changed-row styling).
  // The agGridProps callback wins, as the object does everywhere else.
  const getRowId = useMemo(
    () => (agGridGetRowId ? (row) => agGridGetRowId({ data: row }) : rowGetRowId),
    [agGridGetRowId, rowGetRowId],
  );
  const theme = useTheme();
  const gridRef = useRef(null);
  const hasEnterprise = enterpriseModules.length > 0;

  // Expose grid API through ref
  useImperativeHandle(ref, () => ({
    get api() {
      return gridRef.current?.api;
    },
    get columnApi() {
      return gridRef.current?.columnApi;
    },
    exportToExcel(params = {}) {
      handleExport(params);
    },
    autoSizeColumns() {
      gridRef.current?.api?.autoSizeAllColumns();
    },
    refreshCells(params) {
      gridRef.current?.api?.refreshCells(params);
    },
    clearFilters() {
      handleClearFilters();
    },
    getFilteredData() {
      const data = [];
      gridRef.current?.api?.forEachNodeAfterFilter((node) => {
        if (node.data) data.push(node.data);
      });
      return data;
    },
    getAllData() {
      const data = [];
      gridRef.current?.api?.forEachNode((node) => {
        if (node.data) data.push(node.data);
      });
      return data;
    },
  }));

  // Build column definitions with selection and actions columns
  const finalColumnDefs = useMemo(() => {
    if (!columnDefs) return [];

    // Process columns to make modified_by and modified_date non-editable by default
    let cols = columnDefs.map(col => {
      // If the field is in NON_EDITABLE_FIELDS and editable is not explicitly set, make it non-editable
      if (NON_EDITABLE_FIELDS.includes(col.field) && col.editable === undefined) {
        return { ...col, editable: false };
      }
      return col;
    });

    // Add actions column if configured
    if (showActions) {
      const actionsColumn = createActionsColumn({
        actions,
        onHistory,
        onEdit,
        onDuplicate,
        onDelete,
        isDisabled: isActionDisabled,
        width: actionsColumnWidth,
      });
      cols = [actionsColumn, ...cols];
    }

    // Add selection column if configured (leftmost column)
    if (showSelectionColumn && getRowId) {
      const selectionColumn = createSelectionColumn({
        selectedRows,
        getRowId,
        onRowSelectionChange,
        width: selectionColumnWidth,
        disabled: isSelectionDisabled,
      });
      cols = [selectionColumn, ...cols];
    }

    return cols;
  }, [
    columnDefs,
    showActions,
    actions,
    onHistory,
    onEdit,
    onDuplicate,
    onDelete,
    isActionDisabled,
    actionsColumnWidth,
    showSelectionColumn,
    selectedRows,
    getRowId,
    onRowSelectionChange,
    selectionColumnWidth,
    isSelectionDisabled,
  ]);

  // Cell class getter for change tracking
  const getCellClass = useCallback((params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    if (!data || !field || ['actions', 'select', 'edit'].includes(field)) {
      return '';
    }

    if (!getRowId || !updatedRows) return '';

    const rowId = getRowId(data);
    const rowIsNew = isNewRow ? isNewRow(data) : false;
    const isUpdated = updatedRows[rowId]?.[field];

    if (rowIsNew) {
      return 'ag-cell-updated-new-row';
    } else if (isUpdated) {
      return 'ag-cell-updated';
    }

    return '';
  }, [updatedRows, getRowId, isNewRow]);

  // AG Grid v33+ theme API — switches to dark palette when MUI is in dark mode.
  // The legacy ag-theme-alpine CSS class only styled rows from the MUI cellStyle override
  // below; headers, borders, menus, and hover states stayed light. Using the theme API
  // dark variant fixes all of those consistently.
  const agGridTheme = useMemo(
    () => (theme.palette.mode === 'dark' ? themeQuartz.withPart(colorSchemeDark) : themeQuartz),
    [theme.palette.mode]
  );

  // Default column definition. Cell colors come from the AG Grid theme — don't hardcode
  // them or the dark palette gets overridden and rows go back to looking light.
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 80,
    editable: !isLoading && !isSaving,
    cellClass: updatedRows ? getCellClass : undefined,
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
    lockPinned: false,
    // Merge with user-provided defaults
    ...userDefaultColDef,
  }), [isLoading, isSaving, updatedRows, getCellClass, userDefaultColDef]);

  // Resolve native-selection opt-in. `selectable` may be a boolean or an
  // object of overrides for rowSelection / selectionColumnDef.
  const selectableOpts = useMemo(() => {
    if (!selectable) return null;
    return typeof selectable === 'object' ? selectable : {};
  }, [selectable]);

  // AG Grid rejects autoSizeStrategy alongside flex columns ("colDef.flex is
  // not supported with gridOptions.autoSizeStrategy"): a flex column's width
  // comes from the space left over, not from its contents. So the default
  // fit-to-contents strategy applies only when no column flexes.
  const hasFlexColumn = useMemo(
    () => Boolean(userDefaultColDef?.flex) || (finalColumnDefs || []).some((col) => col && col.flex),
    [finalColumnDefs, userDefaultColDef],
  );

  // Grid options
  const gridOptions = useMemo(() => ({
    rowHeight,
    headerHeight,
    // v32.2+ rowSelection API — object form replaces string + suppress*
    // (suppressRowClickSelection → enableClickSelection:false;
    //  suppressCopyRowsToClipboard/SingleCellRanges defaults → copySelectedRows:true)
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: false,
      copySelectedRows: true,
      // Auto-injected selection column off by default. Apps opt in via the
      // `selectable` prop (preferred) — legacy colDef.checkboxSelection still
      // works in v32.2+ but is deprecated.
      checkboxes: selectableOpts !== null,
      headerCheckbox: selectableOpts !== null,
      selectAll: 'filtered',
      ...selectableOpts,
    },
    // When `selectable` is on, give the auto-injected selection column a
    // sensible pinned/narrow default that matches our prior manual columns.
    ...(selectableOpts !== null && {
      selectionColumnDef: {
        pinned: 'left',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        lockPosition: 'left',
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        ...(selectableOpts.selectionColumnDef || {}),
      },
    }),
    cellSelection: hasEnterprise,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    suppressHorizontalScroll: false,
    alwaysShowVerticalScroll: false,
    animateRows: true,
    undoRedoCellEditing: true,
    ...(!hasFlexColumn && {
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
    }),
    // Context menu (right-click) options - enabled by default with Enterprise
    suppressContextMenu: !hasEnterprise,
    // Column menu options
    suppressMenuHide: false,
    columnMenu: 'new',  // Use new column menu style
    // Clipboard options
    copyHeadersToClipboard: false,
    suppressClipboardPaste: false,
    // Force AG Grid's legacy clipboard path (document.execCommand) instead of
    // navigator.clipboard.readText(). The async Clipboard API requires explicit
    // user permission in Chrome and throws NotAllowedError when the call
    // happens slightly after the user gesture (or in iframes). The workaround
    // is synchronous and avoids the race that wipes the focused cell on paste.
    suppressClipboardApi: true,
    // Strip trailing empty rows from clipboard data (Excel appends \r\n to copied cells,
    // which AG Grid interprets as an extra empty row, wiping the cell below the target)
    processDataFromClipboard: (params) => {
      const data = [...params.data];
      while (data.length > 1 && data[data.length - 1].every(cell => cell === '')) {
        data.pop();
      }
      return data;
    },
  }), [rowHeight, headerHeight, hasEnterprise, selectableOpts, hasFlexColumn]);

  // All modules
  const allModules = useMemo(() => [
    ...BASE_MODULES,
    ...additionalModules,
  ], [additionalModules]);

  // Grid context - passes dynamic state to cell/header components
  const gridContext = useMemo(() => ({
    selectionState: showSelectionColumn ? {
      selectedRows,
      onRowSelectionChange,
      getRowId,
      disabled: isSelectionDisabled,
    } : null,
  }), [showSelectionColumn, selectedRows, onRowSelectionChange, getRowId, isSelectionDisabled]);

  // Caller context (e.g. a log cell's jobName) on top of PamGrid's. While
  // PamGrid runs the selection column its selectionState always wins: the
  // column's renderers need its Set, not a caller's value of the same name.
  const mergedContext = useMemo(() => {
    if (!callerContext) return gridContext;
    const merged = { ...gridContext, ...callerContext };
    if (gridContext.selectionState) merged.selectionState = gridContext.selectionState;
    return merged;
  }, [gridContext, callerContext]);

  // Handle export - respects user column visibility unless columnKeys is explicitly provided
  const handleExport = useCallback((params = {}) => {
    if (onExport) {
      onExport(params);
    } else if (gridRef.current?.api) {
      const mergedConfig = { ...exportConfig, ...params };

      // If no explicit columnKeys, auto-detect visible columns from the grid.
      // This ensures user column selection (show/hide via column menu) is respected.
      if (!mergedConfig.columnKeys) {
        const displayedColumns = gridRef.current.api.getAllDisplayedColumns?.();
        if (displayedColumns) {
          mergedConfig.columnKeys = displayedColumns
            .map(col => col.getColId())
            .filter(id => id !== 'selection' && id !== 'actions');
        }
      }

      const defaultParams = {
        fileName: `export_${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Data',
        exportMode: 'xlsx',
        skipColumnHeaders: false,
        ...mergedConfig,
      };
      gridRef.current.api.exportDataAsExcel(defaultParams);
    }
  }, [onExport, exportConfig]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    // Clear quick filter if using internal state
    onQuickFilterChange?.('');

    // Clear column filters
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
      // The user pressed Clear Filters. An 'api' source would read as
      // programmatic to useGridConfigs and leave the old filter in the stash.
      gridRef.current.api.onFilterChanged('columnFilter');
    }
  }, [onQuickFilterChange]);

  // Handle grid ready
  const handleGridReady = useCallback((params) => {
    setTimeout(() => {
      // The grid can be gone by now (a dialog closed within 100 ms). AG Grid
      // warns (#26) on any call against a destroyed grid.
      if (params.api.isDestroyed?.()) return;
      params.api.autoSizeAllColumns();
    }, 100);
    onGridReady?.(params);
  }, [onGridReady]);

  // Handle first data rendered
  const handleFirstDataRendered = useCallback((params) => {
    params.api.autoSizeAllColumns();
    onFirstDataRendered?.(params);
  }, [onFirstDataRendered]);

  // Cell keydown — PamGrid owns clipboard handling so consumers don't have to.
  //
  // Ctrl+C: call copySelectedRangeToClipboard() (sync write, idempotent with
  //   AG Grid's native handler so no preventDefault needed), then fire the
  //   onCopy callback with scope='range'|'cell' for toast purposes.
  //
  // Ctrl+V: hands off entirely to AG Grid's native paste. Earlier versions
  //   called pasteFromClipboard() here and raced with the native handler —
  //   the async clipboard re-read returned empty on the second call, wiping
  //   the focused (top-left) destination cell. When allowPaste is false, we
  //   preventDefault to block native paste as well.
  //
  // After clipboard handling, the consumer's onCellKeyDown still fires for
  // anything else they care about.
  const handleCellKeyDown = useCallback((params) => {
    const { event, api } = params;
    const isCtrl = event && (event.ctrlKey || event.metaKey);
    if (isCtrl && event.key === 'c') {
      const cellRanges = api.getCellRanges?.();
      const focusedCell = api.getFocusedCell?.();
      if (cellRanges && cellRanges.length > 0) {
        api.copySelectedRangeToClipboard?.();
        onCopy?.({ scope: 'range', api });
      } else if (focusedCell) {
        api.copySelectedRangeToClipboard?.();
        onCopy?.({ scope: 'cell', api });
      }
    } else if (isCtrl && event.key === 'v' && !allowPaste) {
      event.preventDefault?.();
    }
    onCellKeyDown?.(params);
  }, [onCellKeyDown, onCopy, allowPaste]);

  // Register custom cell renderers
  const components = useMemo(() => ({
    ...cellRenderers,
    ...cellEditors,
  }), [cellRenderers, cellEditors]);

  // Merge dark-mode container overrides (e.g. disabled-button color fix) on top
  // of any user-supplied containerSx. Returns {} in light mode so this is a no-op.
  const mergedContainerSx = useMemo(
    () => ({ width: '100%', ...getDarkContainerSx(theme), ...containerSx }),
    [theme, containerSx]
  );

  return (
    <Box sx={mergedContainerSx}>
      {/* Toolbar */}
      {showToolbar && (
        <GridToolbar
          title={title}
          subtitle={subtitle}
          showUpload={showUpload}
          onUpload={onUpload}
          uploadLabel={uploadLabel}
          uploadTooltip={uploadTooltip}
          showTemplate={showTemplate}
          onDownloadTemplate={onDownloadTemplate}
          templateLabel={templateLabel}
          showExport={showExport}
          onExport={handleExport}
          exportLabel={exportLabel}
          showAddRow={showAddRow}
          onAddRow={onAddRow}
          addRowLabel={addRowLabel}
          showSave={showSave}
          onSave={onSave}
          changedRowCount={changedRowCount}
          isSaving={isSaving}
          showClearFilters={showClearFilters}
          onClearFilters={handleClearFilters}
          showBulkDelete={showBulkDelete}
          onBulkDelete={onBulkDelete}
          selectedRowCount={selectedRowCount}
          showDateSelect={showDateSelect}
          availableDates={availableDates}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          isLoadingDates={isLoadingDates}
          isLoading={isLoading}
          customButtons={customToolbarButtons}
          customComponents={customToolbarComponents}
        />
      )}

      {/* Quick Filter */}
      {showQuickFilter && (
        <Box sx={{ mb: 2 }}>
          <QuickFilter
            value={quickFilterText}
            onChange={onQuickFilterChange}
            placeholder={quickFilterPlaceholder}
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      {/* Grid Container */}
      <Box sx={{ position: 'relative', ...gridContainerSx }}>
        <Box
          sx={{
            height,
            width: '100%',
            // Header weight + scrollbar styling. Backgrounds and borders come from the
            // AG theme (themeQuartz / colorSchemeDark) so they read correctly in dark mode.
            '& .ag-header-cell': {
              fontWeight: 600,
            },
            '& .ag-body-viewport::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '& .ag-body-viewport::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.action.disabled,
              borderRadius: '4px',
            },
            '& .ag-body-viewport::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          <style>{GRID_STYLES}</style>
          <AgGridReact
            ref={gridRef}
            theme={agGridTheme}
            rowData={rowData}
            columnDefs={finalColumnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilterText}
            modules={allModules}
            components={components}
            excelStyles={excelStyles}
            context={mergedContext}
            {...gridOptions}
            onGridReady={handleGridReady}
            onFirstDataRendered={handleFirstDataRendered}
            onCellValueChanged={onCellValueChanged}
            onSelectionChanged={onSelectionChanged}
            onRowClicked={onRowClicked}
            onCellFocused={onCellFocused}
            onCellKeyDown={handleCellKeyDown}
            onPasteStart={onPasteStart}
            onPasteEnd={onPasteEnd}
            onFilterChanged={onFilterChanged}
            getRowId={agGridGetRowId || (rowGetRowId ? (params) => rowGetRowId(params.data) : undefined)}
            suppressCellFocus={isLoading || isSaving}
            {...agGridProps}
          />
        </Box>

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {loadingMessage}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer with keyboard shortcuts */}
      {showFooter && (
        <Box
          sx={{
            mt: 1,
            py: 1,
            px: 2,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center',
          }}
        >
          {footerText ? (
            <Typography variant="caption" color="text.secondary">
              {footerText}
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 600, bgcolor: 'grey.200', px: 0.75, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace' }}>Ctrl+C</Box>
                <Box component="span">Copy selected cells</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 600, bgcolor: 'grey.200', px: 0.75, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace' }}>Ctrl+V</Box>
                <Box component="span">Paste into editable cells</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 600, bgcolor: 'grey.200', px: 0.75, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace' }}>Right-click</Box>
                <Box component="span">Context menu (columns, export)</Box>
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Instructions (legacy) */}
      {showInstructions && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {instructions || (
              <>
                <strong>Navigation & Copy/Paste:</strong> Click to select cells, use arrow keys to navigate,
                Shift+arrow keys to select ranges, Ctrl+C to copy, Ctrl+V to paste.
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* Legend */}
      {showLegend && legendItems.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Highlight Legend:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {legendItems.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: item.color,
                    border: item.border || '1px solid #ccc',
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="body2">{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
});

PamGridBase.displayName = 'PamGridBase';

/**
 * ``agGridProps={{ ... }}`` (as useGridConfigs documents for stashBindings)
 * is the same as passing those props directly, and wins on a name clash.
 * Folding it in here keeps PamGrid's own handling of every prop it knows
 * (e.g. ``onCellKeyDown`` still honours ``allowPaste``, ``onGridReady`` still
 * runs PamGrid's setup). A ``context`` passed either way is merged.
 */
export const PamGrid = forwardRef(({ agGridProps, ...props }, ref) => {
  if (!agGridProps) return <PamGridBase ref={ref} {...props} />;
  // ``getRowId`` is the one name whose signature differs: PamGrid's prop takes
  // a row, AG Grid's option takes ``{ data, ... }``. Keep the object's as the
  // AG Grid callback it was written as.
  const { context: objectContext, getRowId: objectGetRowId, ...objectProps } = agGridProps;
  const context = props.context || objectContext
    ? { ...props.context, ...objectContext }
    : undefined;
  return (
    <PamGridBase
      ref={ref}
      {...props}
      {...objectProps}
      context={context}
      agGridGetRowId={objectGetRowId}
    />
  );
});

PamGrid.displayName = 'PamGrid';

export default PamGrid;
